import { createSelector } from '@reduxjs/toolkit';
import { rolesConfig } from 'config/rolesConfig';
import { BILLING_MODULE_CODES, FEATURES_BY_MODULE } from 'config/billingCatalogLabels';
import { buildLicensingPresentation } from 'library/helpers/licensingPresentation';
import { agreementRenewal, cardDisplay } from 'library/helpers/cardValidity';

// Billing selectors — entitlement is the source of truth the UI gates on.
//
// SAFE ROLLOUT (important): existing orgs have module flags ON but NO billing entitlements yet.
// Strict gating would lock the whole fleet out of every module. So entitlement gating is only
// ENFORCED once an org is actually on billing (has ≥1 ACTIVE entitlement). Until then we
// fail OPEN (grandfather) — module flags alone govern, exactly as before billing existed.
//
// Once enforced, "revoke → unavailable" works: a revoked entitlement is no longer ACTIVE, so its
// code drops out of the available set and the module disappears from switcher/sidebar/routes.

const selectBilling = (state) => state.Billing;

const selectSelectedOrgID = (state) => state.Auth?.selectedProfile?.orgID;

// Defence in depth against cross-org leakage: billing loaded for a DIFFERENT org than the selected one
// reads as "no billing data" — which fails OPEN (gating inactive, grandfather-safe), never closed.
// Unknown org on either side passes through (nothing to compare).
export const selectBillingState = createSelector([selectBilling, selectSelectedOrgID], (b, selectedOrgID) => {
	const s = b?.state || null;
	if (!s) return null;
	const loadedFor = b?.orgID ?? s.orgID ?? null;
	if (loadedFor != null && selectedOrgID != null && String(loadedFor) !== String(selectedOrgID)) return null;
	return s;
});

export const selectBillingEntitlements = createSelector([selectBillingState], (s) => s?.entitlements || []);

export const selectBillingLicenseKey = createSelector([selectBillingState], (s) => s?.licenseKey || null);

export const selectBillingLoading = createSelector([selectBilling], (b) => !!b?.loadLoading);

/**
 * The billing read was refused because this person's billing-contact access has ended (S1, D58) — for the SELECTED
 * org. The refusal is bound to the org it was refused for: one for another org (or for no known org) is not this
 * org's, so an org switch never shows the previous org's "access ended" over this org's loading or data.
 */
export const selectBillingAccessEnded = createSelector([selectBilling, selectSelectedOrgID], (b, selectedOrgID) => {
	if (b?.errorCode !== 'STB_BILLING_CONTACT_ENDED') return false;
	const refusedFor = b.errorOrgID ?? null;
	return refusedFor != null && selectedOrgID != null && String(refusedFor) === String(selectedOrgID);
});

export const selectLastDealResult = createSelector([selectBilling], (b) => b?.lastDealResult || null);

// Catalog codes the org is actively entitled to (ACTIVE only).
export const selectEntitledCodes = createSelector([selectBillingEntitlements], (ents) =>
	ents.filter((e) => e.status === 'ACTIVE').map((e) => e.catalogItemCode)
);

// Is entitlement gating ENFORCED for this org? Only once it has ≥1 ACTIVE entitlement (i.e. it is
// genuinely on the billing system). Otherwise fail-open so unprovisioned orgs are never locked out.
export const selectEntitlementGatingActive = createSelector([selectEntitledCodes], (codes) => codes.length > 0);

/**
 * Single-code entitlement check (strict): true iff there is an ACTIVE entitlement for the code.
 * Use for "Upgrade" prompts. For module availability prefer selectIsModuleAvailableByCode, which
 * is grandfather-safe.
 */
export const selectIsEntitled = (catalogCode) =>
	createSelector([selectEntitledCodes], (codes) => !!catalogCode && codes.includes(catalogCode));

/**
 * Grandfather-safe module availability for a catalog code:
 *   - no code             → available (nothing to gate)
 *   - gating not enforced → available (org not on billing yet)
 *   - gating enforced     → available iff the code is ACTIVE
 */
export const selectIsModuleAvailableByCode = (catalogCode) =>
	createSelector([selectEntitledCodes, selectEntitlementGatingActive], (codes, gating) => {
		if (!catalogCode) return true;
		if (!gating) return true;
		return codes.includes(catalogCode);
	});

// ── Subscription lines (Billing page presentation) ──────────────────────────────────────────────
// Derives the org's commercial picture from the billing read-model into display GROUPS:
//   • baseLicence       — the plan itself (CLOUD_S/M/L/E)
//   • paidAddOns        — modules billed as their own subscription item (present in state.items[])
//   • includedInPlan    — modules entitled but not separately billed (come with the plan)
//   • usage             — usage allowances (entitlement.quantityLimit != null, e.g. SMS allowance)
//   • packs             — extra purchasable packs (SMS_PACK_*) currently on the subscription
//   • support           — SUPPORT_* lines
// Each module line nests its packaged FEATURES (Cloud Key / SCIM / Vision under Access), each with
// its own entitlement status — so features render beneath their parent, never as top-level lines.
const FEATURE_CODES = new Set(Object.values(FEATURES_BY_MODULE).flat());
const MODULE_CODES = new Set(BILLING_MODULE_CODES);
const isSmsPack = (code) => /^SMS_PACK/.test(code);
const isSupport = (code) => /^SUPPORT_/.test(code);
// SMS *allowance* (not a pack): the legacy CLOUD_KEY_SMS plus the going-forward PLATFORM_SMS /
// SMS_ALLOWANCE codes. Always surfaced as a usage line even if it carries no explicit cap.
const isSmsAllowance = (code) => /SMS/.test(code) && !isSmsPack(code);

export const selectBillingSubscriptionLines = createSelector([selectBillingState], (s) => {
	if (!s || !s.plan) return null;

	const items = s.items || [];
	const ents = s.entitlements || [];
	const itemByCode = new Map(items.map((i) => [i.catalogItemCode, i]));
	const entByCode = new Map(ents.map((e) => [e.catalogItemCode, e]));

	// Commercial status for a code: ACTIVE / GRACE / NONE (not entitled).
	const statusOf = (code) => {
		const e = entByCode.get(code);
		if (e && (e.status === 'ACTIVE' || e.status === 'GRACE')) return e.status;
		return 'NONE';
	};

	// Packaged features beneath a module — always listed so the packaging is legible (a feature the
	// org doesn't have shows as NONE).
	const featuresFor = (moduleCode) =>
		(FEATURES_BY_MODULE[moduleCode] || []).map((code) => ({ code, status: statusOf(code) }));

	const paidAddOns = [];
	const includedInPlan = [];
	BILLING_MODULE_CODES.forEach((code) => {
		const billed = itemByCode.has(code);
		const status = statusOf(code);
		// Only surface modules the org actually has commercially (entitled or billed). Upsell of
		// not-included modules is the Products page's job, not the commercial summary.
		if (status === 'NONE' && !billed) return;
		// quantity is a paid-add-on concept (it comes off the billed item); included-in-plan lines
		// have no separately billed quantity, so only attach it on the billed branch.
		const line = { code, status, features: featuresFor(code) };
		if (billed) line.quantity = itemByCode.get(code).quantity;
		(billed ? paidAddOns : includedInPlan).push(line);
	});

	// Usage allowances: quantity-limited entitlements (and SMS allowances even when uncapped) that
	// aren't modules/features/packs/support.
	const usage = ents
		.filter(
			(e) =>
				(e.quantityLimit != null || isSmsAllowance(e.catalogItemCode)) &&
				!MODULE_CODES.has(e.catalogItemCode) &&
				!FEATURE_CODES.has(e.catalogItemCode) &&
				!isSmsPack(e.catalogItemCode) &&
				!isSupport(e.catalogItemCode)
		)
		.map((e) => ({ code: e.catalogItemCode, status: e.status, quantityLimit: e.quantityLimit ?? null }));

	// Extra SMS packs currently on the subscription (label-only for now).
	const packs = items
		.filter((i) => isSmsPack(i.catalogItemCode))
		.map((i) => ({ code: i.catalogItemCode, status: 'ACTIVE', quantity: i.quantity }));

	// Support level — from entitlements or billed items, de-duplicated by code.
	const support = [];
	const pushSupport = (code, status) => {
		if (isSupport(code) && !support.some((x) => x.code === code)) support.push({ code, status });
	};
	ents.forEach((e) => pushSupport(e.catalogItemCode, e.status));
	items.forEach((i) => pushSupport(i.catalogItemCode, 'ACTIVE'));

	return { baseLicence: { code: s.plan }, paidAddOns, includedInPlan, usage, packs, support };
});

// Internal billing admin = ZEZAMII STAFF, never a customer role. Invariant: true ONLY when the
// caller holds Developer role 201 in the selected org, OR the server flagged them as platform staff
// (`selectedProfile.isPlatformDeveloper === true`, set by isPlatformDeveloperRequest on the
// self-profile fetch). Holding Global Admin (101) — the CUSTOMER's top role — confers nothing here.
// Mirrors the server gate (App Server helpers/billing/requireInternalBillingAdmin.js
// `isInternalBillingAdmin`: `isDeveloper === true || isPlatformStaffRequest`). The server is
// authoritative; this only decides whether to SHOW the internal tools. Do NOT re-add 101: org
// billing admins (101/102/115) are a separate tier (requireOrgBillingAdmin), not internal admins.
const selectAuthRoles = (state) => state.Auth?.selectedProfile?.roles || {};
const selectProfileIsPlatformStaff = (state) => state.Auth?.selectedProfile?.isPlatformDeveloper === true;
export const selectIsInternalBillingAdmin = createSelector(
	[selectAuthRoles, selectProfileIsPlatformStaff],
	(roles, isPlatformStaff) => {
		if (isPlatformStaff === true) return true;
		const ids = Object.keys(roles || {}).map(Number);
		return ids.includes(rolesConfig.DEVELOPER);
	}
);

// Plain (non-memoized) helper for composing inside other selectors that already have the codes.
export const isCodeAvailable = ({ catalogCode, entitledCodes, gatingActive }) => {
	if (!catalogCode) return true;
	if (!gatingActive) return true;
	return entitledCodes.includes(catalogCode);
};

// ── Site-trial billing (B20) ──────────────────────────────────────────────────────────────────────
// A new-model org's GET /billing carries `commercialModel: 'ACCESS_PER_LOCK_V2'` and `agreement`. A legacy org's
// carries neither (absent means legacy), so every selector below answers null / false for it and the legacy page
// renders exactly as before. Who sees detail is the SERVER's call: a viewer it does not show detail to gets the same
// keys with only `sites[].siteID` + `gate` filled, and `agreement.state === null` is how the UI knows.

export const SITE_TRIAL_MODEL = 'ACCESS_PER_LOCK_V2';

export const selectSiteTrialAgreement = createSelector([selectBillingState], (s) =>
	s?.commercialModel === SITE_TRIAL_MODEL && s.agreement && typeof s.agreement === 'object' ? s.agreement : null
);

export const selectIsSiteTrialCustomer = createSelector([selectSiteTrialAgreement], (a) => a != null);

const selectOrgSites = (state) => state.Sites?.data?.[state.Auth?.selectedProfile?.orgID] || [];

const AGREEMENT_STATE_DISPLAY = {
	TRIAL: { label: 'Grace period', tone: 'info' },
	ACTIVE: { label: 'Active', tone: 'success' },
	CANCEL_SCHEDULED: { label: 'Cancels at period end', tone: 'warning' },
	ENDED: { label: 'Ended', tone: 'danger' }
};
// A TRIAL agreement no site has started yet (the grace period starts at the first qualifying lock placement), F3.
// Customer copy says "grace period", never "trial" (site-trial-billing AMENDMENTS.v1.13 G4).
const TRIAL_NOT_STARTED_DISPLAY = { label: 'Starts at the first lock', tone: 'neutral' };
// A state this build does not know is never shown as its raw code, and never as a confident status.
const UNKNOWN_STATE_DISPLAY = { label: 'Status not confirmed', tone: 'neutral' };
const PAYER_DISPLAY = { ZEZAMII: 'Zezamii (card)', PARTNER: 'Your partner', PENDING: 'Not chosen yet' };
const GATE_TONE = {
	BILLING_SETUP_REQUIRED: 'warning',
	BILLING_PAYMENT_FAILED: 'danger',
	BILLING_AGREEMENT_ENDED: 'danger'
};

/**
 * Display model for the billing page. Pure derivation of the server's answer: labels and tones, site names from the
 * Sites slice (falls back to "Site <id>"), and which gate leads (ended, then payment, then card).
 */
export const selectSiteTrialView = createSelector([selectSiteTrialAgreement, selectOrgSites, selectBillingState], (a, sites, s) => {
	if (!a) return null;
	const names = new Map((sites || []).map((s) => [String(s.siteID), s.name]));
	const showsDetail = a.state != null;
	const siteRows = (a.sites || []).map((s) => ({
		siteID: s.siteID,
		name: names.get(String(s.siteID)) || `Site ${s.siteID}`,
		gate: s.gate || null,
		gateTone: s.gate ? GATE_TONE[s.gate] || 'warning' : 'success',
		trialStartsAt: s.trial?.startsAt ?? null,
		trialEndsAt: s.trial?.endsAt ?? null,
		cardSaved: s.cardSaved ?? null
	}));
	const severity = { BILLING_AGREEMENT_ENDED: 3, BILLING_PAYMENT_FAILED: 2, BILLING_SETUP_REQUIRED: 1 };
	const gated = siteRows.filter((r) => r.gate);
	const leadGate = gated.reduce((top, r) => ((severity[r.gate] || 0) > (severity[top] || 0) ? r.gate : top), null);
	return {
		showsDetail,
		state: a.state ?? null,
		stateDisplay: !showsDetail
			? null
			: a.state === 'TRIAL' && !siteRows.some((r) => r.trialStartsAt)
				? TRIAL_NOT_STARTED_DISPLAY
				: AGREEMENT_STATE_DISPLAY[a.state] || UNKNOWN_STATE_DISPLAY,
		payerLabel: showsDetail ? PAYER_DISPLAY[a.payer] || a.payer : null,
		edition: a.edition ?? null,
		cardState: a.cardState ?? null,
		cardWindowEndsAt: a.cardWindowEndsAt ?? null,
		periodEndsAt: a.periodEndsAt ?? null,
		pendingChange: a.pendingChange ?? null,
		// v1.17: only when the server sent them (its billing.cardValidity flag); undefined keeps today's card row
		card: s && 'card' in s ? cardDisplay(s.card) : undefined,
		renewal: s && 'renewals' in s ? agreementRenewal(s.renewals) : undefined,
		actions: {
			cardUpdate: a.actions?.cardUpdate === true,
			cancel: a.actions?.cancel === true,
			resume: a.actions?.resume === true
		},
		sites: siteRows,
		gatedSiteCount: gated.length,
		leadGate
	};
});

export const selectSiteTrialActionState = createSelector(
	[selectBilling],
	(b) => b?.siteTrialAction || { action: null, loading: false, lastResult: null, error: null }
);

// ── Licensing presentation (Settings › Products & licences, Billing) ──────────────────────────────
// The shared Partner + Cloud vocabulary, derived ONLY from the reads Cloud has (see docs/licensing-ux-data-map.md).
// The read's status is explicit so "not loaded / failed / refused" can never read as "not licensed".
export const selectLicensingReadStatus = createSelector(
	[selectBilling, selectBillingState, selectBillingAccessEnded],
	(b, state, accessEnded) => {
		if (accessEnded) return 'refused';
		if (state) return 'loaded';
		if (b?.loadLoading) return 'loading';
		if (b?.error || b?.errorCode) return 'error';
		return 'loading';
	}
);

/**
 * v1.15 customer licensing read for the SELECTED org: `{ status, data }`. A read made for another org (or one in flight)
 * reads as 'loading' with no data, never as that org's facts.
 */
export const selectCustomerLicensing = createSelector([selectBilling, selectSelectedOrgID], (b, selectedOrgID) => {
	const l = b?.licensing;
	if (!l || l.status === 'idle' || l.status === 'loading') return { status: 'loading', data: null };
	if (l.orgID != null && selectedOrgID != null && String(l.orgID) !== String(selectedOrgID))
		return { status: 'loading', data: null };
	return { status: l.status, data: l.status === 'loaded' ? l.data : null };
});

const selectSiteNameMap = createSelector([selectOrgSites], (sites) =>
	Object.fromEntries((sites || []).map((s) => [String(s.siteID), s.name]))
);

/**
 * buildLicensingPresentation over the billing read, with configuration (module flags) passed in by the caller —
 * `moduleEnabled` is { productKey: boolean } or null while org settings are not loaded.
 */
export const makeSelectLicensingPresentation = () =>
	createSelector(
		[
			selectBillingState,
			selectLicensingReadStatus,
			selectSiteNameMap,
			selectCustomerLicensing,
			(_s, moduleEnabled) => moduleEnabled
		],
		(billing, status, siteNames, licensing, moduleEnabled) =>
			buildLicensingPresentation({ billing, status, siteNames, licensing, moduleEnabled: moduleEnabled || null })
	);
